import { Filter, TrendingUp } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { BarList, PANEL, inputCls } from "./shared";

export function FiltersBar({ filters, onChange, onApply }) {
  const set = (key) => (e) => onChange({ ...filters, [key]: e.target.value });
  return (
    <div className={PANEL}>
      <p className="flex items-center gap-2 text-sm font-bold text-navy mb-3"><Filter className="w-4 h-4 text-gold" /> Filters</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <input type="date" className={inputCls} value={filters.from_date} onChange={set("from_date")} data-testid="filter-from-date" aria-label="From date" />
        <input type="date" className={inputCls} value={filters.to_date} onChange={set("to_date")} data-testid="filter-to-date" aria-label="To date" />
        <input placeholder="Referral source" className={inputCls} value={filters.referral_source} onChange={set("referral_source")} data-testid="filter-referral" />
        <input placeholder="Campaign" className={inputCls} value={filters.campaign} onChange={set("campaign")} data-testid="filter-campaign" />
        <div className="flex gap-2 col-span-2 md:col-span-1">
          <input placeholder="State" className={inputCls} value={filters.state} onChange={set("state")} data-testid="filter-state" />
          <button onClick={onApply} data-testid="filter-apply-button" className="shrink-0 rounded-xl bg-navy text-cream font-bold text-sm px-5 hover:bg-navy-light transition-colors">Apply</button>
        </div>
      </div>
    </div>
  );
}

export function StatCards({ stats }) {
  const cards = stats
    ? [
        ["Assessment Starts", stats.total_starts],
        ["Completed", stats.completed],
        ["Leads Captured", stats.leads],
        ["Lead Capture Rate", `${stats.lead_capture_rate}%`],
        ["Avg. Readiness Score", stats.avg_score],
        ["Appointments", stats.appointments],
        ["Conversion Rate", `${stats.conversion_rate}%`],
        ["Close Rate", `${stats.close_rate}%`],
      ]
    : [];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
      {cards.map(([label, value]) => (
        <div key={label} className="bg-white rounded-2xl border border-sand p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{label}</p>
          <p className="mt-1.5 font-serif text-3xl font-semibold text-navy">{value}</p>
        </div>
      ))}
    </div>
  );
}

export function TrendChart({ trend }) {
  const data = (trend || []).map((d) => ({ ...d, label: new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) }));
  return (
    <div className={PANEL} data-testid="trend-chart">
      <h2 className="font-serif text-2xl font-semibold text-navy mb-1.5 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-gold" /> Last 30 Days
      </h2>
      <p className="text-sm text-slate-500 mb-4">Daily lead volume (bars) and average Legacy Readiness Score (line).</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
            <CartesianGrid stroke="#E5DEC9" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} interval={4} tickLine={false} axisLine={{ stroke: "#E5DEC9" }} />
            <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5DEC9", fontSize: 12 }} formatter={(v, name) => [v ?? "—", name === "avg_score" ? "Avg score" : "Leads"]} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => (v === "avg_score" ? "Avg readiness score" : "Leads captured")} />
            <Bar yAxisId="left" dataKey="leads" fill="#C59B27" radius={[4, 4, 0, 0]} maxBarSize={18} />
            <Line yAxisId="right" dataKey="avg_score" stroke="#0B192C" strokeWidth={2.5} dot={{ r: 2.5, fill: "#0B192C" }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function BreakdownLists({ stats }) {
  const events = Object.entries(stats?.events || {}).map(([name, count]) => ({ name: name.replace(/_/g, " "), count }));
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      <BarList title="Leads by Pathway" items={stats?.pathways || []} testid="leads-by-pathway" />
      <BarList title="Top Lead Sources" items={stats?.top_sources || []} testid="top-sources" />
      <BarList title="Top Campaigns" items={stats?.top_campaigns || []} testid="top-campaigns" />
      <BarList title="Primary Concerns" items={stats?.primary_concerns || []} testid="primary-concerns" />
      <BarList title="Site Events (privacy-safe)" items={events} testid="site-events" />
    </div>
  );
}
